import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import './Dashboard.css';

interface User {
  id: string;
  email: string;
  role: string;
}

interface Simulation {
  id: string;
  name: string;
  description: string;
  scenario: string;
  evaluation_criteria: any;
  created_at: string;
}

interface SimulationAssignment {
  id: string;
  simulation_id: string;
  status: string;
  assigned_at: string;
  due_date?: string;
  simulation: Simulation;
}

interface SimulationResult {
  id: string;
  simulation_id: string;
  certification_status: string;
  completed_at: string;
  simulation: Simulation;
}

interface DashboardProps {
  user: User;
}

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
  const [assignments, setAssignments] = useState<SimulationAssignment[]>([]);
  const [results, setResults] = useState<SimulationResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get participant ID
      const { data: participant } = await supabase
        .from('participants')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!participant) {
        setAssignments([]);
        setResults([]);
        return;
      }

      // Get assigned simulations
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('simulation_assignments')
        .select(`
          *,
          simulation:simulations(*)
        `)
        .eq('participant_id', participant.id)
        .order('assigned_at', { ascending: false });

      if (assignmentError) throw assignmentError;

      // Get completed simulation results
      const { data: resultData, error: resultError } = await supabase
        .from('simulation_results')
        .select(`
          id,
          simulation_id,
          certification_status,
          completed_at,
          simulation:simulations(*)
        `)
        .eq('participant_id', participant.id)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });

      if (resultError) throw resultError;

      setAssignments(assignmentData || []);
      setResults((resultData || []) as any);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned': return '#ffa500';
      case 'in_progress': return '#2196f3';
      case 'completed': return '#4caf50';
      case 'overdue': return '#f44336';
      default: return '#666';
    }
  };

  const getCertificationColor = (status: string) => {
    switch (status) {
      case 'certified': return '#4caf50';
      case 'not_certified': return '#f44336';
      case 'pending': return '#ffa500';
      default: return '#666';
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Welcome back, {user.email.split('@')[0]}!</h1>
        <p>Your MedTech sales training dashboard</p>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      <div className="dashboard-content">
        {/* Custom Practice Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #0f1923 0%, #1a2f3f 100%)',
            borderRadius: '12px',
            padding: '28px 32px',
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap' as const,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(0,153,153,0.2)',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: "'Oswald', Arial, sans-serif",
                fontSize: '20px',
                fontWeight: 700,
                color: '#009999',
                textTransform: 'uppercase' as const,
                letterSpacing: '1.5px',
                margin: '0 0 8px',
              }}
            >
              Start Custom Practice
            </h2>
            <p
              style={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '14px',
                margin: 0,
                maxWidth: '420px',
                lineHeight: '1.5',
              }}
            >
              Build a scenario around any account, persona, and practice focus — no assignment needed.
            </p>
          </div>
          <Link
            to="/cartridge"
            style={{
              display: 'inline-block',
              padding: '12px 28px',
              background: 'linear-gradient(135deg, #009999, #007777)',
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '14px',
              textDecoration: 'none',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap' as const,
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(0,153,153,0.35)',
            }}
          >
            Build My Scenario
          </Link>
        </div>

        {/* Assigned Simulations */}
        <div className="dashboard-section">
          <h2>Assigned Simulations</h2>
          {assignments.length === 0 ? (
            <div className="empty-state">
              <p>No simulations assigned yet.</p>
              <p>Contact your training administrator to get started.</p>
            </div>
          ) : (
            <div className="simulation-grid">
              {assignments.map((assignment) => (
                <div key={assignment.id} className="simulation-card">
                  <div className="simulation-header">
                    <h3>{assignment.simulation.name}</h3>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(assignment.status) }}
                    >
                      {assignment.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="simulation-description">
                    {assignment.simulation.description}
                  </p>
                  <div className="simulation-meta">
                    <p>Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}</p>
                    {assignment.due_date && (
                      <p>Due: {new Date(assignment.due_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="simulation-actions">
                    {assignment.status === 'assigned' && (
                      <Link 
                        to={`/simulation/${assignment.simulation_id}`}
                        className="btn btn-primary"
                      >
                        Start Simulation
                      </Link>
                    )}
                    {assignment.status === 'in_progress' && (
                      <Link 
                        to={`/simulation/${assignment.simulation_id}`}
                        className="btn btn-secondary"
                      >
                        Continue Simulation
                      </Link>
                    )}
                    {assignment.status === 'completed' && (
                      <Link 
                        to={`/results/${assignment.simulation_id}`}
                        className="btn btn-outline"
                      >
                        View Results
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completed Simulations */}
        {results.length > 0 && (
          <div className="dashboard-section">
            <h2>Completed Simulations</h2>
            <div className="results-grid">
              {results.map((result) => (
                <div key={result.id} className="result-card">
                  <div className="result-header">
                    <h3>{result.simulation.name}</h3>
                    <span 
                      className="certification-badge"
                      style={{ backgroundColor: getCertificationColor(result.certification_status) }}
                    >
                      {result.certification_status}
                    </span>
                  </div>
                  <div className="result-meta">
                    <p>Completed: {new Date(result.completed_at).toLocaleDateString()}</p>
                  </div>
                  <div className="result-actions">
                    <Link 
                      to={`/results/${result.simulation_id}`}
                      className="btn btn-outline"
                    >
                      View Detailed Report
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resource Library Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            borderRadius: '12px',
            padding: '24px 32px',
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            flexWrap: 'wrap' as const,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            border: '1px solid rgba(147,51,234,0.25)',
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: "'Oswald', Arial, sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                color: '#c084fc',
                textTransform: 'uppercase' as const,
                letterSpacing: '1.5px',
                margin: '0 0 6px',
              }}
            >
              📚 Resource Library
            </h2>
            <p
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: '14px',
                margin: 0,
                maxWidth: '380px',
                lineHeight: '1.5',
              }}
            >
              Clinical evidence, legal &amp; regulatory guides, and pre-read materials for your simulations.
            </p>
          </div>
          <Link
            to="/library"
            style={{
              display: 'inline-block',
              padding: '10px 24px',
              background: 'linear-gradient(135deg, #9333ea, #7c3aed)',
              color: '#fff',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '14px',
              textDecoration: 'none',
              whiteSpace: 'nowrap' as const,
              flexShrink: 0,
              boxShadow: '0 4px 16px rgba(147,51,234,0.35)',
            }}
          >
            Browse Library
          </Link>
        </div>

        {/* Admin Panel Access */}
        {user.role === 'admin' && (
          <div className="dashboard-section">
            <div className="admin-access">
              <h2>Administrator Tools</h2>
              <p>Access the administrative panel to manage participants, simulations, and view analytics.</p>
              <Link to="/admin" className="btn btn-primary">
                Open Admin Panel
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
